import { useState, type DragEvent } from "react";
import { Eye, EyeOff, X } from "lucide-react";
import { Button, DragHandle, Icon, IconButton, Skeleton, dragType, useDropTarget } from "@cntrl/ui";
import { errorMessage } from "../../app/hooks/apiError.js";
import { pushToast } from "../../app/toasts.js";
import { openSettingsTab } from "../../app/settingsPanel.js";
import {
  MERCH_SHELF_MAX,
  useMerchShelf,
  useUpdateMerchShelf,
  type MerchShelfProduct,
  type MerchShelfRead,
} from "../../app/hooks/useLinkInBio.js";
import styles from "./MerchShelfSection.module.scss";

const STORE_NAMES: Record<string, string> = { shopify: "Shopify", square: "Square", fourthwall: "Fourthwall" };

/**
 * The Link-in-Bio MERCH section (ART-234) — which products from the band's connected store the public page shows.
 * Lives under Assets › Link in Bio for now and is self-contained (its own read, its own PUT, saved on each change)
 * so it can move to a merch page without dragging the links editor along.
 *
 * Tap a product to put it on the shelf (up to six, in the order tapped); tap again to take it off. With nothing
 * picked the page shows the store's first six — so the numbered badges come from the SERVER's `showing` list, never
 * re-derived here: the badge is what the page will actually draw.
 *
 * The ORDER is set in the "On your page" strip above the catalogue (PO, ART-234 feedback): six numbered slots in
 * page order, each dragged by its grip (arrow keys on the grip move it too). The strip shows `showing`, so a band
 * still on the first-six default can drag those as well; the first move writes that order down as its picks.
 *
 * The catalogue is a half-size row that scrolls sideways (a band can carry a lot of merch), and a product is
 * DRAGGED from it straight into a slot (PO, ART-234 round two) — onto an empty slot it joins the end, onto a filled
 * one it takes that position, replacing the product there when all six are full. Tapping a tile still adds or
 * removes it: HTML drag-and-drop has no keyboard or touch path, so the tap is the accessible way to do the same.
 */
export function MerchShelfSection() {
  const q = useMerchShelf();
  const save = useUpdateMerchShelf();

  const put = (next: Pick<MerchShelfRead, "picks" | "hidden">, ok?: string) =>
    save.mutate(
      { picks: [...(next.picks ?? [])], hidden: next.hidden ?? false },
      {
        onSuccess: () => { if (ok) pushToast(ok, "success"); },
        onError: (e) => pushToast(errorMessage(e, "Couldn't save your merch."), "error"),
      },
    );

  return (
    <section className={styles.section} aria-labelledby="links-merch-heading">
      <header className={styles.head}>
        <h3 id="links-merch-heading" className={styles.title}>Merch</h3>
        {q.data?.provider && (
          <button type="button" className={styles.toggle} aria-pressed={q.data.hidden ?? false}
            disabled={save.isPending}
            aria-label={q.data.hidden ? "Show merch on your page" : "Hide merch from your page"}
            onClick={() => put({ picks: q.data!.picks, hidden: !q.data!.hidden },
              q.data!.hidden ? "Merch is back on your page." : "Merch hidden from your page.")}>
            <Icon icon={q.data.hidden ? EyeOff : Eye} size={16} />
            <span>{q.data.hidden ? "Hidden" : "On page"}</span>
          </button>
        )}
      </header>
      <Body q={q} saving={save.isPending} onPicks={(picks) => put({ picks, hidden: q.data?.hidden ?? false })} />
    </section>
  );
}

function Body({ q, saving, onPicks }: {
  q: ReturnType<typeof useMerchShelf>;
  saving: boolean;
  onPicks: (picks: string[]) => void;
}) {
  if (!q.data) {
    return q.isError
      ? <p className={styles.note}>Couldn't load your store's products. <Button size="xs" variant="ghost" onClick={() => void q.refetch()}>Retry</Button></p>
      : <div role="status" aria-label="Loading merch" className={styles.grid}>{[0, 1, 2].map((i) => <Skeleton key={i} variant="block" height={150} />)}</div>;
  }

  const shelf = q.data;
  const store = shelf.provider ? STORE_NAMES[shelf.provider] ?? shelf.provider : null;

  // Three absences, three different sentences: no store (theirs to connect), connected but never synced (ours —
  // it arrives with the next merch sync), and synced with nothing shelf-able (theirs — products need a photo and a
  // store page).
  if (!store) {
    return (
      <p className={styles.note}>
        Connect a Shopify or Square store to put your merch on your page.{" "}
        <button type="button" className={styles.link} onClick={() => openSettingsTab("connections")}>Open Connections</button>
      </p>
    );
  }
  if (!shelf.syncedOn) {
    return <p className={styles.note}>Your {store} products haven't synced yet — they appear here after the next merch sync.</p>;
  }
  const products = shelf.products ?? [];
  if (products.length === 0) {
    return <p className={styles.note}>No {store} products to show — a product needs a photo and a page on your store.</p>;
  }

  const picks = (shelf.picks ?? []).filter((id) => products.some((p) => p.id === id));
  const showing = shelf.showing ?? [];
  const full = picks.length >= MERCH_SHELF_MAX;
  const toggle = (id: string) =>
    onPicks(picks.includes(id) ? picks.filter((p) => p !== id) : [...picks, id]);
  // The strip edits the list the page DRAWS. On the default (no picks) that is the server's first six, so the
  // first reorder or removal materialises them as picks rather than acting on an empty list.
  const order = showing.filter((id) => products.some((p) => p.id === id));
  const move = (from: number, to: number) => {
    const next = [...order];
    const [item] = next.splice(from, 1);
    if (item === undefined) return;
    next.splice(to, 0, item);
    onPicks(next);
  };
  const remove = (id: string) => onPicks(order.filter((p) => p !== id));
  // A catalogue product dropped on slot `at`: past the last filled slot it joins the end; on a filled slot it takes
  // that position — inserted while there is room, REPLACING the product there once all six are full.
  const place = (id: string, at: number) => {
    if (!products.some((p) => p.id === id)) return;
    const next = order.filter((p) => p !== id);
    if (at >= next.length) {
      if (next.length >= MERCH_SHELF_MAX) return;
      next.push(id);
    } else if (next.length >= MERCH_SHELF_MAX) {
      next[at] = id;
    } else {
      next.splice(at, 0, id);
    }
    onPicks(next);
  };

  return (
    <>
      <p className={styles.note}>
        {picks.length === 0
          ? `Drag up to ${MERCH_SHELF_MAX} products onto your page, or tap them. Until you do, your first ${MERCH_SHELF_MAX} from ${store} show.`
          : `${picks.length} of ${MERCH_SHELF_MAX} picked from ${store}. Drag products up to add them, and drag the slots to set the order.`}
      </p>
      <div className={styles.strip}>
        <span className={styles.stripLabel}>On your page</span>
        <ol className={styles.slots} aria-label="Merch on your page, in order">
          {Array.from({ length: MERCH_SHELF_MAX }, (_, i) => {
            const id = order[i];
            const product = id ? products.find((p) => p.id === id) : undefined;
            return product ? (
              <ShelfSlot key={id} product={product} index={i} count={order.length}
                disabled={saving} onMove={move} onPlace={place} onRemove={() => remove(id!)} />
            ) : (
              <EmptySlot key={`empty-${i}`} index={i} disabled={saving} onPlace={place} />
            );
          })}
        </ol>
      </div>
      <ul className={styles.catalogue} aria-label={`Your ${store} products`} data-hidden={shelf.hidden || undefined}>
        {products.map((p) => (
          <ProductTile key={p.id} product={p}
            picked={picks.includes(p.id ?? "")}
            slot={showing.indexOf(p.id ?? "")}
            disabled={saving || (full && !picks.includes(p.id ?? ""))}
            draggable={!saving}
            onToggle={() => p.id && toggle(p.id)} />
        ))}
      </ul>
    </>
  );
}

const SHELF_DRAG_KIND = "merch-shelf";
/** A catalogue product dragged up into a slot — its own kind, carrying the product ID rather than a slot index. */
const ADD_DRAG_KIND = "merch-shelf-add";

/** Drop props for a slot that accepts a catalogue product. Ignores every other drag, so it composes with the
 *  slot's own reorder target. */
function useAddDrop(index: number, disabled: boolean, onPlace: (id: string, at: number) => void) {
  const [isOver, setIsOver] = useState(false);
  const type = dragType(ADD_DRAG_KIND);
  return {
    isOver,
    onDragOver: (e: DragEvent<HTMLElement>) => {
      if (disabled || !e.dataTransfer.types.includes(type)) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = "copy";
      setIsOver(true);
    },
    onDragLeave: () => setIsOver(false),
    onDrop: (e: DragEvent<HTMLElement>) => {
      setIsOver(false);
      const id = e.dataTransfer.getData(type);
      if (!id || disabled) return;
      e.preventDefault();
      onPlace(id, index);
    },
  };
}

function EmptySlot({ index, disabled, onPlace }: {
  index: number;
  disabled: boolean;
  onPlace: (id: string, at: number) => void;
}) {
  const { isOver, ...add } = useAddDrop(index, disabled, onPlace);
  return <li className={styles.slotEmpty} aria-hidden="true" data-drag-over={isOver || undefined} {...add}>{index + 1}</li>;
}

function ShelfSlot({ product, index, count, disabled, onMove, onPlace, onRemove }: {
  product: MerchShelfProduct;
  index: number;
  count: number;
  disabled: boolean;
  onMove: (from: number, to: number) => void;
  onPlace: (id: string, at: number) => void;
  onRemove: () => void;
}) {
  const drop = useDropTarget({ index, kind: SHELF_DRAG_KIND, onMove });
  const { isOver, ...add } = useAddDrop(index, disabled, onPlace);
  const name = product.title ?? "Product";
  // Both targets ignore the other's drags, so each event simply goes to both.
  return (
    <li className={styles.slot}
      data-drag-over={drop["data-drag-over"] || isOver || undefined}
      onDragOver={(e) => { drop.onDragOver(e); add.onDragOver(e); }}
      onDragLeave={() => { drop.onDragLeave(); add.onDragLeave(); }}
      onDrop={(e) => { drop.onDrop(e); add.onDrop(e); }}>
      <span className={styles.slotShot}>
        {product.imageUrl && <img src={product.imageUrl} alt="" loading="lazy" referrerPolicy="no-referrer" draggable={false} />}
        <span className={styles.badge} data-picked aria-hidden="true">{index + 1}</span>
      </span>
      <span className={styles.slotName}>{name}</span>
      <span className={styles.slotActions}>
        {!disabled && (
          <DragHandle label={name} index={index} count={count} kind={SHELF_DRAG_KIND} onMove={onMove} />
        )}
        <IconButton icon={X} label={`Remove ${name} from position ${index + 1}`} size="xs" disabled={disabled} onClick={onRemove} />
      </span>
    </li>
  );
}

function ProductTile({ product, picked, slot, disabled, draggable, onToggle }: {
  product: MerchShelfProduct;
  picked: boolean;
  slot: number;
  disabled: boolean;
  draggable: boolean;
  onToggle: () => void;
}) {
  const name = product.title ?? "Product";
  // Draggable even when the shelf is full and the tap is disabled: a drop onto a filled slot replaces it.
  return (
    <li className={styles.catalogueItem} draggable={draggable && !!product.id}
      onDragStart={(e) => {
        if (!product.id) return;
        e.dataTransfer.setData(dragType(ADD_DRAG_KIND), product.id);
        e.dataTransfer.effectAllowed = "copy";
      }}>
      <button type="button" className={styles.tile} aria-pressed={picked} disabled={disabled}
        aria-label={picked ? `Take ${name} off your page` : `Put ${name} on your page`} onClick={onToggle}>
        <span className={styles.shot}>
          {product.imageUrl && <img src={product.imageUrl} alt="" loading="lazy" referrerPolicy="no-referrer" draggable={false} />}
          {slot >= 0 && (
            // The position the PAGE draws it in — numbered for picks and for the first-six default alike.
            <span className={styles.badge} data-picked={picked || undefined} aria-hidden="true">
              {slot + 1}
            </span>
          )}
        </span>
        <span className={styles.name}>{name}</span>
        {product.price && <span className={styles.price}>{product.price}</span>}
      </button>
    </li>
  );
}
